import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Settings, Loader2, ShieldAlert, Lock, Clock } from 'lucide-react';

/**
 * Provider Status panel — admin can flip each settlement layer on/off in
 * real time. Backed by /api/admin/provider-status, no backend restart needed.
 * Used inside the Settlement Overview tab.
 */
const ProviderStatusPanel = ({ api, onChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [reasonDraft, setReasonDraft] = useState({}); // {providerId: 'reason'}
  const [autoReenable, setAutoReenable] = useState({}); // {providerId: bool}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/provider-status');
      setRows(res.data?.providers || []);
    } catch {
      toast.error('Failed to load provider status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (row, enabled) => {
    if (row.env_override_disabled) {
      toast.error(`${row.label} is hard-blocked by env override — cannot toggle from UI`);
      return;
    }
    setSavingId(row.id);
    try {
      await api.post(`/admin/provider-status/${row.id}`, {
        enabled,
        reason: !enabled ? (reasonDraft[row.id] || 'Manually disconnected from Admin UI') : '',
        auto_reenable_hours: (!enabled && autoReenable[row.id]) ? 24 : null,
      });
      toast.success(`${row.label} ${enabled ? 'enabled' : (autoReenable[row.id] ? 'disabled — auto re-enables in 24h' : 'disabled')}`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update provider status');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card className="p-6" data-testid="provider-status-panel">
      <div className="flex items-center gap-3 mb-3">
        <Settings className="w-5 h-5 text-slate-600" />
        <h3 className="text-xl font-semibold text-slate-800">Provider Status</h3>
        <Badge variant="outline" className="text-slate-600 border-slate-300">
          Live toggle — no restart needed
        </Badge>
        <Button size="sm" variant="ghost" onClick={load} className="ml-auto" data-testid="provider-status-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Switch a provider OFF to instantly block new settlements (existing data + audit trail stays intact, HTTP 503 returned on submit). Env-overridden providers are locked with a 🔒 icon and can only be re-enabled by removing them from <code className="bg-slate-100 px-1 rounded">DISABLED_SETTLEMENT_PROVIDERS</code> in the backend env.
      </p>

      {loading && rows.length === 0 ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="provider-status-grid">
          {rows.map((row) => {
            const isEnv = row.env_override_disabled;
            const isEnabled = row.effective_enabled;
            const saving = savingId === row.id;
            return (
              <div
                key={row.id}
                className={`border rounded-lg p-3 transition ${
                  isEnabled
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : 'border-rose-200 bg-rose-50/40'
                }`}
                data-testid={`provider-row-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-slate-800 truncate">{row.label}</p>
                      {isEnv && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1 py-0.5" title="Env-overridden — change in backend .env to unlock">
                          <Lock className="w-2.5 h-2.5" /> env
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-snug">{row.description}</p>
                  </div>
                  <Switch
                    checked={isEnabled}
                    disabled={isEnv || saving}
                    onCheckedChange={(v) => toggle(row, v)}
                    data-testid={`provider-switch-${row.id}`}
                  />
                </div>
                {!isEnabled && (
                  <div className="mt-2 border-t border-rose-200 pt-2">
                    <p className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> Blocked
                    </p>
                    {row.reason && <p className="text-[11px] text-rose-700/90 mt-0.5">Reason: {row.reason}</p>}
                    {row.disabled_by && (
                      <p className="text-[10px] text-rose-600/80 mt-0.5">
                        by {row.disabled_by} • {row.disabled_at ? new Date(row.disabled_at).toLocaleString() : ''}
                      </p>
                    )}
                    {row.auto_reenable_at && (
                      <p className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`provider-autoreenable-${row.id}`}>
                        <Clock className="w-2.5 h-2.5" /> Auto re-enables: {new Date(row.auto_reenable_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                {isEnabled && !isEnv && (
                  <div className="mt-2 space-y-1">
                    <Input
                      placeholder="Reason (optional, e.g. 'Pending KYC')"
                      value={reasonDraft[row.id] || ''}
                      onChange={(e) => setReasonDraft({ ...reasonDraft, [row.id]: e.target.value })}
                      className="h-7 text-[11px]"
                      data-testid={`provider-reason-${row.id}`}
                    />
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!autoReenable[row.id]}
                        onChange={(e) => setAutoReenable({ ...autoReenable, [row.id]: e.target.checked })}
                        className="h-3 w-3 accent-indigo-600"
                        data-testid={`provider-autoreenable-toggle-${row.id}`}
                      />
                      Auto re-enable after 24h
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default ProviderStatusPanel;
